import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Send,
  Refresh,
  Comment as CommentIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { FiTrash2 } from "react-icons/fi";
import { colors } from "../theme/modernTheme";
import { contactsAPI } from "../services/api";
import { showSuccess, showError } from "../utils/toast";
import { useAuth } from "../context/AuthContext";

export default function CommentModal({
  open,
  onClose,
  contactId,
  contactName,
}) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState("");
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const commentsEndRef = useRef(null);

  const canEdit = user?.role === "ADMIN" || user?.role === "STAFF";

  useEffect(() => {
    if (open && contactId) {
      loadComments();
    }
  }, [open, contactId]);

  const loadComments = async () => {
    if (!contactId) return;

    setCommentsLoading(true);
    setCommentsError("");

    try {
      const response = await contactsAPI.getContactComments(contactId);
      setComments(response.data || []);
    } catch (error) {
      setCommentsError(error?.error?.message || "Failed to load comments");
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!contactId) {
      showError("Cannot add comment: No contact associated");
      return;
    }

    setSubmittingComment(true);
    try {
      const response = await contactsAPI.createContactComment(contactId, {
        comment_text: newComment,
      });
      setComments([...comments, response.data]);
      setNewComment("");
      showSuccess("Comment added successfully");
      // Scroll to bottom after adding comment
      setTimeout(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (error) {
      showError(error?.error?.message || "Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!contactId) {
      showError("Cannot delete comment: No contact associated");
      return;
    }

    try {
      await contactsAPI.deleteContactComment(contactId, commentId);
      setComments(comments.filter((c) => c.id !== commentId));
      showSuccess("Comment deleted successfully");
    } catch (error) {
      showError(error?.error?.message || "Failed to delete comment");
    }
  };

  const handleClose = () => {
    setNewComment("");
    setCommentsError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "4px",
          height: "700px",
          display: "flex",
          flexDirection: "column",
        },
      }}>
      <DialogTitle
        sx={{
          p: 2,
          borderBottom: `2px solid ${colors.primary.main}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: colors.primary.main,
              fontSize: "0.875rem",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              mb: 0.5,
            }}>
            Comments
          </Typography>
          {contactName && (
            <Typography
              variant="body1"
              sx={{
                color: colors.text.secondary,
                fontSize: "0.95rem",
                fontWeight: 500,
                mt: 0.5,
                textTransform: "uppercase",
              }}>
              {contactName}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: colors.text.secondary,
            p: 0.5,
            "&:hover": {
              color: colors.text.primary,
              backgroundColor: "transparent",
            },
          }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}>
        {commentsLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}>
            <CircularProgress />
          </Box>
        ) : commentsError ? (
          <Box p={3}>
            <Alert
              severity="error"
              action={
                <IconButton size="small" onClick={loadComments}>
                  <Refresh />
                </IconButton>
              }>
              {commentsError}
            </Alert>
          </Box>
        ) : (
          <>
            {/* Comments Feed */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 3,
                backgroundColor: "#ffffff",
              }}>
              {comments.length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%">
                  <CommentIcon sx={{ fontSize: 64, color: "#ddd", mb: 2 }} />
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 1 }}>
                    No comments yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start the conversation by adding a comment below
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {comments.map((comment, index) => (
                    <React.Fragment key={comment.id}>
                      <Box
                        ref={
                          index === comments.length - 1 ? commentsEndRef : null
                        }
                        sx={{
                          mb: 1,
                        }}>
                        {/* Author and timestamp header */}
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                backgroundColor: colors.primary.main,
                                color: "#ffffff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: "0.875rem",
                              }}>
                              {comment.author_name.charAt(0).toUpperCase()}
                            </Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: "#000000",
                                fontSize: "0.875rem",
                              }}>
                              {comment.author_name}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#737B8B",
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}>
                              {new Date(comment.created_at).toLocaleString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </Typography>
                            {canEdit &&
                              (user.role === "ADMIN" ||
                                comment.author_id === user.id) && (
                                <Tooltip title="Delete comment">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDeleteComment(comment.id)
                                    }
                                    sx={{
                                      color: colors.text.primary,
                                      "&:hover": {
                                        color: colors.error.main,
                                      },
                                    }}>
                                    <FiTrash2 size={16} />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </Box>
                        </Box>

                        {/* Comment text */}
                        <Box
                          sx={{
                            p: "10px",
                          }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: colors.text.primary,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              lineHeight: 1.5,
                              fontSize: "0.9rem",
                              fontWeight: 500,
                              backgroundColor: "#F7FAFC",
                              p: 1.5,
                              borderRadius: "4px",
                              display: "inline-block",
                              maxWidth: "100%",
                            }}>
                            {comment.comment_text}
                          </Typography>
                        </Box>
                      </Box>
                      {index < comments.length - 1 && (
                        <Divider sx={{ mb: 2 }} />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>

            {/* Comment Input */}
            {canEdit && (
              <Box
                sx={{
                  mb: 0.5,
                  p: 1.5,
                  borderTop: "1px solid #e5e7eb",
                  backgroundColor: colors.background.sidebar,
                }}>
                <Box display="flex" gap={1} alignItems="flex-start">
                  <Box sx={{ flex: 1, position: "relative" }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={3}
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 2000) {
                          setNewComment(value);
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (newComment.trim() && !submittingComment) {
                            handleAddComment();
                          }
                        }
                      }}
                      inputProps={{
                        maxLength: 2000,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          padding: "8px",
                          borderRadius: "4px",
                          backgroundColor: "#ffffff",
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        bottom: "-20px",
                        right: 0,
                        fontSize: "0.75rem",
                        color: "text.secondary",
                      }}>
                      {newComment.length}/2000
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submittingComment}
                    startIcon={
                      submittingComment ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Send />
                      )
                    }
                    sx={{
                      backgroundColor: colors.primary.main,
                      color: colors.secondary.contrastText,
                      textTransform: "none",
                      borderRadius: "4px",
                      fontWeight: 600,
                      px: 2,
                      minWidth: "80px",
                      height: "40px",
                      mt: 0,
                      "&:hover": {
                        backgroundColor: colors.primary.dark,
                      },
                      "&:disabled": {
                        backgroundColor: colors.action.row,
                        color: colors.text.disabled,
                      },
                    }}>
                    {submittingComment ? "Posting..." : "Post"}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
